import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { getCockpitMetrics } from '@/api/clarity'
import { getKpiGoals } from '@/api/kpi'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { AllBranchKpisResponseModel } from '@/types'
import { SalesRegionBoxMetricsModel } from '@/types/clarity'
import { dateToString, getMappedRegionBranches } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'
import { areDateParamsValid } from '@/utils/filterParams'

export default function useSalesRegionBoxMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(() => areDateParamsValid(startDate), [startDate])
  const loading = !isReady || isFetching

  const [branchData, setBranchData] = useState<SalesRegionBoxMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<
    SalesRegionBoxMetricsModel[]
  >([])
  const { regionBranchMap, branches } = getMappedRegionBranches()
  const [processedData, setProcessedData] = useState<
    SalesRegionBoxMetricsModel[]
  >([])
  const [branchKpiMap, setBranchKpiMap] = useState<Map<string, number>>(
    new Map()
  )
  const [regionKpiMap, setRegionKpiMap] = useState<Map<string, number>>(
    new Map()
  )

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics } = await fetchSalesRegionBoxMetrics(
          branches,
          startDate,
          endDate,
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) =>
            metric.stand !== 'system_total' && metric.region !== 'Other'
        )

        setBranchData(filteredBranchMetrics)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics } = await fetchSalesRegionBoxMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to'),
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) => metric.stand !== 'system_total'
        )

        setBranchTrendData(filteredBranchMetrics)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [startDate, endDate, dateFilterType])

  const fetchKpiGoals = useMemo(() => {
    return async () => {
      try {
        const response = await getKpiGoals(startDate, endDate, 'sales')
        if (response && 'branches' in response) {
          const kpiResponse = response as AllBranchKpisResponseModel
          if (kpiResponse.branches) {
            setBranchKpiMap(
              new Map(kpiResponse.branches.map((b) => [b.name, b.value]))
            )
          }
          if (kpiResponse.regions) {
            setRegionKpiMap(
              new Map(kpiResponse.regions.map((r) => [r.name, r.value]))
            )
          }
        }
      } catch (err) {
        console.error('Error fetching KPI goals:', err)
      }
    }
  }, [startDate, endDate])

  const getProcessedData = useCallback(
    (
      currentData: SalesRegionBoxMetricsModel[],
      trendData: SalesRegionBoxMetricsModel[]
    ) => {
      if (!currentData.length || !trendData.length) return []

      const currentDataMap = new Map(
        currentData.map((item) => [item.stand, item])
      )
      const trendDataMap = new Map(trendData.map((item) => [item.stand, item]))
      const commonStands = [...currentDataMap.keys()].filter((stand) =>
        trendDataMap.has(stand)
      )

      const standsByRegion = commonStands.reduce((acc, stand) => {
        const region = currentDataMap.get(stand)?.region || ''
        const current = currentDataMap.get(stand)?.current
        const trend = trendDataMap.get(stand)?.current

        if (!acc.has(region)) {
          acc.set(region, [])
        }

        acc.get(region)?.push({
          stand: stand,
          region: region,
          kpiValue: stand ? (branchKpiMap.get(stand) ?? null) : null,
          current: {
            adns: current?.adns || 0,
            traffic: current?.traffic || 0,
            avgCheck: current?.avgCheck || 0,
            totalDiscountsRatio: current?.totalDiscountsRatio || 0,
          },
          prev: {
            adns: trend?.adns || 0,
            traffic: trend?.traffic || 0,
            avgCheck: trend?.avgCheck || 0,
            totalDiscountsRatio: trend?.totalDiscountsRatio || 0,
          },
        })

        return acc
      }, new Map<string, SalesRegionBoxMetricsModel[]>())

      const sortedStandsByRegion = new Map(
        [...standsByRegion.entries()].sort((a, b) => {
          const regionA = a[0].toLowerCase()
          const regionB = b[0].toLowerCase()
          return regionA.localeCompare(regionB)
        })
      )

      // Create parent regions with children stands
      const processedData = Array.from(sortedStandsByRegion.entries()).map(
        ([region, stands]) => {
          // Create parent region with aggregated metrics
          const regionData: SalesRegionBoxMetricsModel = {
            stand: region,
            kpiValue: regionKpiMap.get(region) ?? null,
            current: {
              adns: stands.reduce(
                (sum, stand) => sum + (stand.current?.adns || 0),
                0
              ),
              traffic: stands.reduce(
                (sum, stand) => sum + (stand.current?.traffic || 0),
                0
              ),
              avgCheck:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.avgCheck || 0),
                  0
                ) / stands.length,
              totalDiscountsRatio:
                stands.reduce(
                  (sum, stand) =>
                    sum + (stand.current?.totalDiscountsRatio || 0),
                  0
                ) / stands.length,
            },
            prev: {
              adns: stands.reduce(
                (sum, stand) => sum + (stand.prev?.adns || 0),
                0
              ),
              traffic: stands.reduce(
                (sum, stand) => sum + (stand.prev?.traffic || 0),
                0
              ),
              avgCheck:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.avgCheck || 0),
                  0
                ) / stands.length,

              totalDiscountsRatio:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.totalDiscountsRatio || 0),
                  0
                ) / stands.length,
            },
            children: stands,
          }

          return regionData
        }
      )

      setProcessedData(processedData as SalesRegionBoxMetricsModel[])
    },
    [branchData, branchTrendData, branchKpiMap, regionKpiMap]
  )

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      await Promise.all([fetchData(), fetchTrendData(), fetchKpiGoals()])
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [fetchData, fetchTrendData, fetchKpiGoals, isReady])

  const refetch = useCallback(() => {
    console.log('Refetching sales region box data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    if (branchData.length && branchTrendData.length) {
      getProcessedData(branchData, branchTrendData)
    }
  }, [
    branchData,
    branchTrendData,
    branchKpiMap,
    regionKpiMap,
    getProcessedData,
  ])

  return {
    processedData,
    loading,
    refetch,
  }
}

async function fetchSalesRegionBoxMetrics(
  branches: string[],
  startDate: string,
  endDate: string,
  regionBranchMap: Map<string, string[]>
): Promise<{
  branchMetrics: SalesRegionBoxMetricsModel[]
}> {
  try {
    const response = await getCockpitMetrics(
      branches.join(','),
      startDate,
      endDate
    )

    const metrics: SalesRegionBoxMetricsModel[] = response.map(
      (responseMetrics) =>
        setMetric(
          responseMetrics.branch_name,
          getBranchRegion(responseMetrics.branch_name, regionBranchMap),
          responseMetrics.adns,
          responseMetrics.traffic,
          responseMetrics.average_check,
          responseMetrics.total_discounts_ratio
        )
    )

    const branchMetrics = metrics

    return {
      branchMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function getBranchRegion(
  branchName: string,
  regionMap: Map<string, string[]>
): string {
  for (const [region, branches] of regionMap.entries()) {
    if (branches.includes(branchName)) {
      return region
    }
  }
  return 'Undefined Region'
}

function setMetric(
  stand: string,
  region: string,
  adns: number,
  traffic: number,
  avgCheck: number,
  totalDiscountsRatio: number
): SalesRegionBoxMetricsModel {
  return {
    stand: stand,
    region: region,
    current: {
      adns: formatMetric(adns, 0),
      traffic: formatMetric(traffic, 0),
      avgCheck: formatMetric(avgCheck, 2),
      totalDiscountsRatio: formatMetric(totalDiscountsRatio * 100, 1),
    },
  }
}
